/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useEffect, useRef, useState } from 'react'

import { FiSearch, FiX } from 'react-icons/fi'

import type { DropdownOption } from '@/components/ui'
import { Button } from '@/components/ui'

import { ACTION_META, ACTION_OPTIONS, ENTITY_META, ENTITY_OPTIONS, TIME_PRESETS } from './constants'
import type { FilterSelectOption } from './FilterSelect'
import { FilterSelect } from './FilterSelect'
import { formatCount, formatRangeLabel, localInputToUtc, utcToLocalInput } from './format'
import type { AuditFacets, AuditFilters, ChangeType, EntityType, TimePreset } from './types'

interface FilterBarProps {
  filters: AuditFilters
  companyOptions: DropdownOption[]
  facets?: AuditFacets
  isSearching: boolean
  activeCount: number
  onChange: (patch: Partial<AuditFilters>) => void
  onClear: () => void
}

/**
 * A control whose value differs from the default gets a primary ring, so the user can see at a
 * glance why they are seeing so few rows without opening every dropdown.
 */
const activeRing = (active: boolean) => (active ? 'rounded-field ring-1 ring-primary' : '')

// ---- Time range ------------------------------------------------------------

const TimeRange: React.FC<{
  filters: AuditFilters
  onChange: (patch: Partial<AuditFilters>) => void
}> = ({ filters, onChange }) => {
  const [open, setOpen] = useState(false)
  const [from, setFrom] = useState(utcToLocalInput(filters.from))
  const [to, setTo] = useState(utcToLocalInput(filters.to))
  const popoverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  const isCustom = filters.time === 'custom'
  const customLabel = isCustom ? formatRangeLabel(filters.from, filters.to) || 'Custom' : 'Custom'

  const apply = () => {
    onChange({ time: 'custom', from: localInputToUtc(from), to: localInputToUtc(to) })
    setOpen(false)
  }

  return (
    <div className="relative flex items-center" ref={popoverRef}>
      <div className="join" role="group" aria-label="Time range">
        {TIME_PRESETS.map((preset) => (
          <button
            key={preset.value}
            type="button"
            aria-pressed={filters.time === preset.value}
            onClick={() => onChange({ time: preset.value as TimePreset, from: undefined, to: undefined })}
            className={`btn join-item btn-sm ${filters.time === preset.value ? 'btn-primary' : 'btn-outline'}`}
          >
            {preset.label}
          </button>
        ))}
        <button
          type="button"
          aria-pressed={isCustom}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className={`btn join-item btn-sm ${isCustom ? 'btn-primary' : 'btn-outline'}`}
        >
          {customLabel}
        </button>
      </div>

      {open && (
        <div className="absolute left-0 top-full z-40 mt-1 w-72 rounded-box border border-base-300 bg-base-100 p-3 shadow-lg">
          <label className="mb-2 block text-xs font-medium">
            From
            <input
              type="datetime-local"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="input input-sm input-bordered mt-1 w-full"
            />
          </label>
          <label className="mb-3 block text-xs font-medium">
            To
            <input
              type="datetime-local"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="input input-sm input-bordered mt-1 w-full"
            />
          </label>
          <div className="flex justify-end gap-2">
            <Button variation="ghost" size="sm" handleClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variation="primary" size="sm" isDisabled={!from || !to} handleClick={apply}>
              Apply
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

// ---- Filter bar ------------------------------------------------------------

export const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  companyOptions,
  facets,
  isSearching,
  activeCount,
  onChange,
  onClear,
}) => {
  // Local mirror so typing stays responsive; the committed value is debounced below.
  const [search, setSearch] = useState(filters.search)
  const committed = useRef(filters.search)

  // Adopt an externally-driven change (Clear all, a pasted URL) without fighting the user's typing.
  useEffect(() => {
    if (filters.search !== committed.current) {
      committed.current = filters.search
      setSearch(filters.search)
    }
  }, [filters.search])

  useEffect(() => {
    if (search === committed.current) return
    const timer = setTimeout(() => {
      committed.current = search
      onChange({ search })
    }, 400)
    return () => clearTimeout(timer)
  }, [search, onChange])

  // The hierarchy path is what tells two similarly-named companies apart, so it is rendered after
  // the name and searched with it — the same treatment the shared Dropdown gives it.
  const companySelectOptions: FilterSelectOption[] = companyOptions.map((option) => ({
    value: option.value,
    label: option.label,
    hint:
      option.fullName && option.fullName.startsWith(option.label)
        ? option.fullName.slice(option.label.length)
        : option.fullName,
  }))

  const actionOptions: FilterSelectOption[] = ACTION_OPTIONS.map((value) => ({
    value,
    label: ACTION_META[value].label,
    icon: ACTION_META[value].icon,
  }))

  const entityOptions: FilterSelectOption[] = ENTITY_OPTIONS.map((value) => ({
    value,
    label: ENTITY_META[value].label,
    icon: ENTITY_META[value].icon,
  }))

  // The user list comes from the facets: only people who actually made changes in this period,
  // each with a count. Far more useful than every user in the company.
  const userOptions: FilterSelectOption[] = (facets?.users ?? []).map((user) => ({
    value: user.userId,
    label: user.userName ?? `User #${user.userId}`,
    meta: formatCount(user.count),
  }))

  return (
    <div className="rounded-box border border-base-300 bg-base-100 p-3">
      <div className="flex flex-wrap items-center gap-2">
        {/* Search */}
        <div className={`relative w-[280px] ${activeRing(!!filters.search)}`}>
          <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base-content/50" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search entity name…"
            aria-label="Search entity name"
            className="input input-sm input-bordered w-full pl-9 pr-9"
          />
          {isSearching && (
            <span className="loading loading-spinner loading-xs absolute right-8 top-1/2 -translate-y-1/2" />
          )}
          {search && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-base-content/50 hover:text-base-content"
            >
              <FiX />
            </button>
          )}
        </div>

        {/* Company — defaults to the app's parent company, but may be emptied: the table then asks
            for a company rather than guessing one back on the user's behalf. */}
        <FilterSelect
          label="Companies"
          className="w-[240px]"
          searchable
          options={companySelectOptions}
          selected={filters.companyIds.map(String)}
          onChange={(values) => onChange({ companyIds: values.map(Number) })}
        />

        <TimeRange filters={filters} onChange={onChange} />
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <FilterSelect
          label="Actions"
          className="w-[180px]"
          options={actionOptions}
          selected={filters.changeTypes}
          onChange={(values) => onChange({ changeTypes: values as ChangeType[] })}
        />

        <FilterSelect
          label="Entities"
          className="w-[210px]"
          options={entityOptions}
          selected={filters.entityTypes}
          onChange={(values) => onChange({ entityTypes: values as EntityType[] })}
        />

        <FilterSelect
          label="Users"
          className="w-[220px]"
          searchable
          emptyText="No users made changes in this period."
          options={userOptions}
          selected={filters.userIds}
          onChange={(values) => onChange({ userIds: values })}
        />

        {activeCount > 0 && (
          <div className="ml-auto">
            <Button variation="ghost" size="sm" handleClick={onClear}>
              Clear all
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
